import React, { Fragment } from 'react';
import Select, { CSSObjectWithLabel } from 'react-select';
import { IMultiSelectFilter } from './types';
import { Typography, useTheme } from '@material-ui/core';

/**
 * Represents a select filter component for the lead list.
 *
 * @param {*} {
 *     label,
 *     options,
 *     value,
 *     onChange,
 *     isMulti = true,
 *     isClearable = true,
 *     placeholder,
 *     ...props
 * }
 * @return {*}
 */
export const MultiSelectFilter = ({
    label,
    options,
    value,
    onChange,
    isMulti = true,
    isClearable = true,
    placeholder,
    ...props
}: IMultiSelectFilter) => {
    const theme = useTheme();
    const { styles, ...rest } = props;

    const selectBaseStyles = {
        control: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            cursor: 'pointer',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid #C9C9C9`,
        }),
        valueContainer: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            height: 42,
            borderRight: `1px solid #C9C9C9`,
            scrollbarColor: `${theme.palette.divider} transparent`,
            '&::-webkit-scrollbar': {
                width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.divider,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.background.paper,
            },
        }),
        placeholder: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            fontSize: 16,
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.text.secondary,
        }),
        option: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            fontSize: 16,
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.background.paper,
        }),
        multiValue: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color: theme.palette.text.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontFamily: theme.typography.fontFamily,
            padding: 3,
            border: `1px solid #C9C9C9`,
            backgroundColor: theme.palette.background.paper,
        }),
        menuList: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            backgroundColor: theme.palette.background.paper,
        }),
        multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color: theme.palette.text.primary,
        }),
    };

    return (
        <Fragment>
            <Typography
                variant="h6"
                style={{ minWidth: 85 }}
                data-testid="multi-select-filter-label"
            >
                {label}
            </Typography>
            <Select
                options={options}
                value={value}
                onChange={onChange}
                isMulti={isMulti}
                isClearable={isClearable}
                placeholder={placeholder || label}
                styles={{ ...selectBaseStyles, ...styles }}
                inputId="multi-select-filter-input"
                menuPosition="fixed"
                {...rest}
            />
        </Fragment>
    );
};
